import { mdiClose, mdiDownload, mdiPlus, mdiUpload } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import { useAuth } from "../../../../../context_providers/auth/authProvider";
import { useOrderDetailCtx } from "../../../../../context_providers/order_detail_context";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";
import { MODULE, ACTION } from "../../../../../utils/enums";
import ProductRowWidgetV2 from "../../order_create/product/product_rowV2";
import ProductRowWidgetWithUnitV2 from "../../order_create/product/product_row_with_unit";
import ItemBulkUpload from "../../order_create/product/ItemBulkUpload";
import {
  buildValidateRow,
  downloadSampleTemplate,
} from "../../order_create/product/bulk_item_utils";

interface ProductSectionProps {}

// Serialized Finished Goods belong only in the Products/FG section. Non-Serialized
// Finished Goods stay searchable here: the Products row only accepts "Finished Goods",
// so excluding them too left those items addable nowhere.
const FINISHED_GOODS_SUBCATEGORIES = ["Finished Goods"];

const ProductSection: FunctionComponent<ProductSectionProps> = () => {
  const { user, isAuthorised, hasRoleTitle } = useAuth();
  const {
    order,
    getProducts,
    onProductAdded,
    onProductDeleted,
    onProductUpdated,
  } = useOrderDetailCtx();
  const [addNewProduct, setAddNewProduct] = useState(false);
  const [addNewService, setAddNewService] = useState(false);
  const [orderView, setOrderView] = useState(false);
  const [addNewRawMaterial, setAddNewRawMaterial] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [formId, setFormId] = useState(0);
  const [isEditAllowed, setIsEditAllowed] = useState(false);
  const [bulkModal, setBulkModal] = useState<
    null | "product" | "service" | "raw_material"
  >(null);

  useEffect(() => {
    const loggedInUser = user?.uid;
    const orderCreatedBy = order.created_by;
    const hasOrderCreatorAccess = hasRoleTitle({
      roleTitle: "Create orders only",
    });

    // Allow edit if the logged in user created the order, or has order creator role
    if (
      (loggedInUser === orderCreatedBy || hasOrderCreatorAccess) &&
      order?.update_restrict !== true
    ) {
      setIsEditAllowed(true);
    } else {
      setIsEditAllowed(false);
    }

    const order_view_permission = isAuthorised({
      module: MODULE.order_view,
      action: [ACTION.READ],
    });

    setOrderView(order_view_permission);

    setDisabled(
      !isAuthorised({ module: MODULE.order, action: [ACTION.UPDATE] }),
    );
    getProducts();
  }, [order, user]);

  // Categorize order items
  const productsList = order.products ?? [];
  const servicesList = order.services ?? [];
  const rawMaterialsList = order.raw_materials ?? [];

  const bulkSection =
    bulkModal === "raw_material" ? "raw" : (bulkModal ?? "product");

  const bulkExisting = () =>
    bulkModal === "product"
      ? productsList
      : bulkModal === "service"
        ? servicesList
        : rawMaterialsList;

  const bulkTitle =
    bulkModal === "product"
      ? "Bulk Upload Products"
      : bulkModal === "service"
        ? "Bulk Upload Services"
        : "Bulk Upload Raw Materials";

  const handleBulkComplete = async (items: OrderItemModelV2[]) => {
    if (!bulkModal || items.length === 0) return;
    for (const it of items) {
      await onProductAdded({ ...it }, bulkModal);
    }
    setFormId(Math.random());
  };

  const renderBulkButtons = (
    category: "product" | "service" | "raw_material",
  ) =>
    !disabled && isEditAllowed ? (
      <>
        <button
          type="button"
          onClick={downloadSampleTemplate}
          className="flex items-center gap-1 text-xs border border-myPrimaryColor text-myPrimaryColor px-2 py-1 rounded hover:bg-green-50 transition-colors"
        >
          <Icon path={mdiDownload} className="h-4 w-4" />
          Download Sample Template
        </button>
        <button
          type="button"
          onClick={() => setBulkModal(category)}
          className="flex items-center gap-1 text-xs bg-myPrimaryColor text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
        >
          <Icon path={mdiUpload} className="h-4 w-4" />
          Upload CSV
        </button>
      </>
    ) : null;

  // Reusable Section Component
  const renderSection = (
    title: string,
    items: OrderItemModelV2[],
    addNewState: boolean,
    setAddNewState: (value: boolean) => void,
    category: "product" | "service" | "raw_material",
  ) => {
    // Restrict the item picker to the right type/subcategory per section, so
    // a Finished Good can't be added under Services and vice versa.
    const itemSearchType = category === "service" ? "Service" : "Product";
    const itemSearchSubcategory =
      category === "product" ? "Finished Goods" : undefined;
    return (
    <>
      <div className="flex justify-between items-end my-2">
        <h3 className="font-semibold text-gray-500">{title}</h3>
        <div className="flex items-center gap-2">
          {renderBulkButtons(category)}
          {!disabled && isEditAllowed && (
            <div
              onClick={() => setAddNewState(!addNewState)}
              className="flex items-center gap-1 cursor-pointer text-xs bg-myPrimaryColor text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
            >
              <Icon
                path={addNewState ? mdiClose : mdiPlus}
                className="h-4 w-4"
              />
              {addNewState ? "Cancel" : "Add"}
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:block bg-white rounded-md shadow py-3 px-5 mb-2 text-sm">
        <div className="grid grid-cols-9 gap-3">
          <h4 className="font-semibold text-left">Item Code</h4>
          <h4 className="font-semibold text-left">
            <span className="text-base text-red-500">*</span> Item Name
          </h4>
          <h4 className="font-semibold text-center">Description</h4>
          <h4 className="font-semibold text-center">
            <span className="text-base text-red-500">*</span> Quantity
          </h4>
          <h4 className="font-semibold text-center">Price per unit (excl. GST)</h4>
          <h4 className="font-semibold text-center">GST %</h4>
          <h4 className="font-semibold text-center">Final Price (incl. GST)</h4>
          <h4 className="font-semibold text-center">Amount</h4>
          <h4 className="font-semibold text-center">Action</h4>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-sm">
        {items.map((item) => (
          <ProductRowWidgetV2
            edit={false}
            canEdit={orderView}
            key={item.id}
            item={item}
            item_search_type={itemSearchType}
            item_search_subcategory={itemSearchSubcategory}
            onDelete={(data) => {
              onProductDeleted({ ...data }, category);
              setFormId(Math.random());
            }}
            onSubmit={(data) => {
              onProductUpdated({ ...data }, category);
              setFormId(Math.random());
            }}
            isEditAllowed={isEditAllowed}
          />
        ))}

        {addNewState && !disabled && (
          <div className="border-dashed border-2 rounded-md border-green-300 p-2 text-sm">
            <ProductRowWidgetV2
              key={formId}
              edit={true}
              item_search_type={itemSearchType}
              item_search_subcategory={itemSearchSubcategory}
              onSubmit={(data) => {
                if (window.confirm("Are you sure?")) {
                  onProductAdded({ ...data }, category);
                  setFormId(Math.random());
                }
              }}
              isEditAllowed={isEditAllowed}
            />
            <div className="flex justify-between">
              <p className="text-sm italic text-gray-400">
                Please select product and qty then hit + to add product.
              </p>
              <p className="text-sm italic text-gray-400">
                Note: Enter the base price (excluding GST) — GST is applied automatically
                from the item's tax slab, and the final GST-inclusive price
                is what's used for the order.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
    );
  };

  return (
    <>
      <div>
        <div>
          <div className="flex justify-between items-end my-1">
            <h3 className="font-semibold text-gray-500">Order Items</h3>
          </div>

          {renderSection(
            "Products",
            productsList,
            addNewProduct,
            setAddNewProduct,
            "product",
          )}
          {renderSection(
            "Services",
            servicesList,
            addNewService,
            setAddNewService,
            "service",
          )}

          <section>
            <div className="flex justify-between items-end my-2">
              <h3 className="font-semibold text-gray-500">Raw Materials</h3>
              <div className="flex items-center gap-2">
                {!orderView && renderBulkButtons("raw_material")}
                {!disabled && isEditAllowed && !orderView && (
                  <div
                    onClick={() => setAddNewRawMaterial(!addNewRawMaterial)}
                    className="flex items-center gap-1 cursor-pointer text-xs bg-myPrimaryColor text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
                  >
                    <Icon
                      path={addNewRawMaterial ? mdiClose : mdiPlus}
                      className="h-4 w-4"
                    />
                    {addNewRawMaterial ? "Cancel" : "Add"}
                  </div>
                )}
              </div>
            </div>
            <div className="hidden md:block bg-white rounded-md shadow py-3 px-5 mb-2 text-sm">
              <div className="grid grid-cols-10 gap-3">
                <h4 className="font-semibold text-left">Item Code</h4>
                <h4 className="font-semibold text-left">
                  <span className="text-base text-red-500">*</span> Item Name
                </h4>
                <h4 className="font-semibold text-left">Description</h4>
                <h4 className="font-semibold text-left">
                  <span className="text-base text-red-500">*</span> Item Unit
                </h4>
                <h4 className="font-semibold text-center">
                  <span className="text-base text-red-500">*</span> Quantity
                </h4>
                <h4 className="font-semibold text-center">Price per unit (excl. GST)</h4>
                <h4 className="font-semibold text-center">GST %</h4>
                <h4 className="font-semibold text-center">Final Price (incl. GST)</h4>
                <h4 className="font-semibold text-center">Amount</h4>
                <h4 className="font-semibold text-center">Action</h4>
              </div>
            </div>
            <div className="flex flex-col gap-2 text-sm">
              {rawMaterialsList.map((item) => (
                <ProductRowWidgetWithUnitV2
                  edit={false}
                  key={item.id}
                  onEdit={orderView}
                  isEditAllowed={isEditAllowed}
                  item={item}
                  item_search_type="Product"
                  item_search_exclude_subcategory={FINISHED_GOODS_SUBCATEGORIES}
                  onDelete={(data) => {
                    onProductDeleted({ ...data }, "raw_material");
                    setFormId(Math.random());
                  }}
                  onSubmit={(data) => {
                    onProductUpdated({ ...data }, "raw_material");
                    setFormId(Math.random());
                  }}
                />
              ))}

              {addNewRawMaterial && !disabled && (
                <div className="border-dashed border-2 rounded-md border-green-300 p-2 text-sm">
                  <ProductRowWidgetWithUnitV2
                    key={formId}
                    edit={true}
                    item_search_type="Product"
                    item_search_exclude_subcategory={FINISHED_GOODS_SUBCATEGORIES}
                    onSubmit={(data) => {
                      if (window.confirm("Are you sure?")) {
                        onProductAdded({ ...data }, "raw_material");
                        setFormId(Math.random());
                      }
                    }}
                  />
                  <div className="flex justify-between">
                    <p className="text-sm italic text-gray-400">
                      Please select product and qty then hit + to add product.
                    </p>
                    <p className="text-sm italic text-gray-400">
                      Note: Enter the base price (excluding GST) — GST is applied automatically
                from the item's tax slab, and the final GST-inclusive price
                is what's used for the order.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {bulkModal && (
        <ItemBulkUpload
          title={bulkTitle}
          validateRow={buildValidateRow(bulkSection, bulkExisting)}
          onComplete={handleBulkComplete}
          onClose={() => setBulkModal(null)}
        />
      )}
    </>
  );
};

export default ProductSection;
